import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '@/shared/api/http';
import type { Language, MenuLayout } from '@/shared/api/types';
import { useAuthStore } from '@/shared/auth/store';
import { ensureLanguage, i18n } from '@/shared/i18n/i18n';
import { isSupportedLanguage } from '@/shared/i18n/languages';
import { useThemeStore } from '@/shared/theme/store';

/** `GET /api/admin/settings/public` (anonymous): what the shell shows before and after sign-in. */
export interface PublicPortalSettings {
  companyName: string;
  /** Anonymous, versioned logo URL; null = the Orbita mark. */
  logoUrl: string | null;
  /** `#rrggbb`; null = the default accent. */
  accentColor: string | null;
  defaultLanguage: Language;
  defaultTimeZone: string;
  /** The default arrangement of the left menu for everybody; null: the natural order. */
  menu?: MenuLayout | null;
}

/**
 * Query key owned by the admin module (it updates the cache after saving and on `admin.settings-updated`); the shell
 * only reads it.
 */
export const PORTAL_SETTINGS_QUERY_KEY = ['admin', 'settings', 'public'] as const;

/** Language the visitor chose on this device (`src/shared/i18n/i18n.ts`, read by `public/boot.js`). */
const LANGUAGE_STORAGE_KEY = 'orbita.language';

export function usePortalSettings() {
  return useQuery({
    queryKey: PORTAL_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => api.get<PublicPortalSettings>('/admin/settings/public', { auth: false, signal }),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

function hasChosenLanguage(): boolean {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) !== null;
  } catch {
    return true;
  }
}

/**
 * Applies the portal settings to the shell: the accent color (CSS variable, remembered for the next page load) and,
 * for visitors who never picked a language and are not signed in (signed-in users get their own), the default language.
 */
export function usePortalBranding(): void {
  const { data } = usePortalSettings();
  const status = useAuthStore((s) => s.status);
  const accent = data?.accentColor;
  const defaultLanguage = data?.defaultLanguage;

  useEffect(() => {
    if (accent !== undefined) useThemeStore.getState().setAccent(accent);
  }, [accent]);

  useEffect(() => {
    if (status !== 'anonymous' || !isSupportedLanguage(defaultLanguage) || hasChosenLanguage()) return;
    if (i18n.language !== defaultLanguage)
      void ensureLanguage(defaultLanguage).then(() => i18n.changeLanguage(defaultLanguage));
  }, [status, defaultLanguage]);
}
